import { Point, rotatePointAbout } from "common/geometry";

// That's x, y
export type BoundingBox = Readonly<{
    vertex1: Point;
    vertex2: Point;
    vertex3: Point;
    vertex4: Point;

    width: number;
    height: number;
}>;

export function computeBoundingBox(points: readonly Point[], theta: number): BoundingBox {
    if (points.length === 0) {
        return {
            vertex1: [0, 0],
            vertex2: [0, 0],
            vertex3: [0, 0],
            vertex4: [0, 0],
            width: 0,
            height: 0,
        };
    }

    let xAvg = 0;
    let yAvg = 0;
    for (const xy of points) {
        xAvg += xy[0];
        yAvg += xy[1];
    }

    xAvg /= points.length;
    yAvg /= points.length;

    const s = Math.sin(-theta);
    const c = Math.cos(-theta);

    let minX = Number.MAX_SAFE_INTEGER;
    let maxX = Number.MIN_SAFE_INTEGER;
    let minY = Number.MAX_SAFE_INTEGER;
    let maxY = Number.MIN_SAFE_INTEGER;

    for (const xy of points) {
        const xT = xy[0] - xAvg;
        const yT = xy[1] - yAvg;
        const xR = c * xT - s * yT;
        const yR = s * xT + c * yT;
        if (xR < minX) {
            minX = xR;
        }
        if (xR > maxX) {
            maxX = xR;
        }
        if (yR < minY) {
            minY = yR;
        }
        if (yR > maxY) {
            maxY = yR;
        }
    }

    const rc = Math.cos(theta);
    const rs = Math.sin(theta);

    return {
        vertex1: [rc * maxX - rs * maxY + xAvg, rs * maxX + rc * maxY + yAvg],
        vertex2: [rc * minX - rs * maxY + xAvg, rs * minX + rc * maxY + yAvg],
        vertex3: [rc * minX - rs * minY + xAvg, rs * minX + rc * minY + yAvg],
        vertex4: [rc * maxX - rs * minY + xAvg, rs * maxX + rc * minY + yAvg],
        width: maxX - minX,
        height: maxY - minY,
    };
}

export type InfiniteLine = Readonly<{
    radius: number;
    theta: number;
}>;

// In order to represent the line in polar notation,
// 1. We determine the length of the line between the points
// 2. We compute the sine and cosine on the basis of their definition relative
//    to the length of the line as the hypotenuse
// 3. We rotate the first point about the X, Y origin
// 4. We ignore the X distance and compute the Y distance from the Y origin
//    according to this rotated line
function linesForPoints(p1: Point, p2: Point): InfiniteLine {
    const xD = p2[0] - p1[0];
    const yD = p2[1] - p1[1];
    const hyp = Math.sqrt(xD * xD + yD * yD);
    const s = -yD / hyp;
    const c = xD / hyp;

    const radius = s * p1[0] + c * p1[1];
    const theta = Math.acos(c);
    return { radius, theta };
}

export type LinesForBoundingBox = Readonly<{
    line1: InfiniteLine; // vertex1 -> vertex2
    line2: InfiniteLine; // vertex2 -> vertex3
    line3: InfiniteLine; // vertex3 -> vertex4
    line4: InfiniteLine; // vertex4 -> vertex1
}>;

export function linesForBoundingBox(box: BoundingBox): LinesForBoundingBox {
    return {
        line1: linesForPoints(box.vertex1, box.vertex2),
        line2: linesForPoints(box.vertex2, box.vertex3),
        line3: linesForPoints(box.vertex3, box.vertex4),
        line4: linesForPoints(box.vertex4, box.vertex1),
    };
}

export function directionAngleForBoundingBox(box: BoundingBox): number {
    const dx = box.vertex4[0] - box.vertex3[0];
    const dy = box.vertex4[1] - box.vertex3[1];

    const s = dy / box.width;
    const c = dx / box.width;

    let theta = Math.acos(c);
    if (s < 0) {
        theta = 2 * Math.PI - theta;
    }
    return theta;
}

export function correctionAngleForBoundingBox(box: BoundingBox): number {
    return -directionAngleForBoundingBox(box);
}

export function boundingBoxForBoundingBox(box: BoundingBox, padX: number, padY: number): BoundingBox {
    const minX = Math.min(box.vertex1[0], box.vertex2[0], box.vertex3[0], box.vertex4[0]) - padX;
    const maxX = Math.max(box.vertex1[0], box.vertex2[0], box.vertex3[0], box.vertex4[0]) + padX;
    const minY = Math.min(box.vertex1[1], box.vertex2[1], box.vertex3[1], box.vertex4[1]) - padY;
    const maxY = Math.max(box.vertex1[1], box.vertex2[1], box.vertex3[1], box.vertex4[1]) + padY;

    return {
        vertex1: [maxX, maxY],
        vertex2: [minX, maxY],
        vertex3: [minX, minY],
        vertex4: [maxX, minY],
        width: maxX - minX,
        height: maxY - minY,
    };
}

export function vert3OffsetsForBoundingBox(box: BoundingBox, padX: number, padY: number): [number, number] {
    const { vertex1, vertex2, vertex3, vertex4, width, height } = box;
    let xAvg = vertex1[0] + vertex2[0] + vertex3[0] + vertex4[0];
    let yAvg = vertex1[1] + vertex2[1] + vertex3[1] + vertex4[1];
    xAvg /= 4;
    yAvg /= 4;

    const theta = directionAngleForBoundingBox(box);

    const targetX = xAvg - width / 2 - padX;
    const targetY = yAvg - height / 2 - padY;

    return rotatePointAbout(targetX, targetY, theta, xAvg, yAvg);
}
